export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
}

export const seoConfig: Record<string, SEOConfig> = {
  home: {
    title: "AI Presentation & Document Builder | Zynth AI Tools Platform",
    description: "Zynth helps you create AI-powered presentations and documents faster using intelligent tools built for modern workflows and productivity.",
    keywords: "ai presentation, ai document builder, ai tools, ai presentation maker, ai document, ai presentation generator, presentation ai, document builder, slides ai, ai tools online",
  },
  about: {
    title: "About Zynth AI | Building Smarter AI Content Tools",
    description: "Learn how Zynth is building advanced AI tools for creating presentations and documents with speed, accuracy, and flexibility.",
    keywords: "artificial intelligence tools, ai document intelligence, document artificial intelligence, ai powered presentation, ai powered presentations, best ai tools, all ai tools, new ai tools, ai online tools, tools ai",
  },
  newPresentation: {
    title: "AI Presentation Generator | Create Slides Instantly",
    description: "Generate stunning slide decks using AI technology designed to transform ideas into professional presentations instantly.",
    keywords: "ai presentation generator, ai slides generator, presentation maker ai, ai slide maker, presentation generator ai, ai slide deck, ai presentation builder, ai create presentation, create presentation with ai, ai presentation creator",
  },
  newDocument: {
    title: "AI Document Builder | Generate Structured Content",
    description: "Create structured, professional documents using AI-powered drafting, formatting, and intelligent content generation.",
    keywords: "ai document, document on artificial intelligence, builders document, ai for documents, document, document artificial intelligence, document builder ai, ai document creation, intelligent document tools, ai content document",
  },
  extension: {
    title: "Google Slides AI Add-on | Create Slides Faster",
    description: "Use Zynth's Google Slides AI add-on to generate presentations directly inside Google Slides using AI.",
    keywords: "ai slides add on, create slides with extension, Add on to create slides, google slides ai add on, ai add on for google slides, text to slides ai google, ai slides generator google, ai slide presentation, presentation ai google add on, ai slides google workspace",
  },
  pricing: {
    title: "Zynth AI Pricing | Affordable AI Creation Plans",
    description: "Explore flexible pricing plans for AI presentation and document generation tailored for individuals and teams.",
    keywords: "free ai presentation maker, ai presentation pricing, presentation ai free, ai document builder pricing, free ai presentation, best value ai presentation software, free ai ppt maker, free ai presentation generator, affordable ai presentation tool, free ai tool for presentation",
  },
  apiReference: {
    title: "Zynth AI API | Integrate AI Presentation Tools",
    description: "Access Zynth's AI APIs to integrate presentation generation into your applications seamlessly.",
    keywords: "ai presentation api, ai presentation tools api, ai slides api, ai slide deck api, ai content generation api, generate presentations via api, text to presentation api, ai automation api, ai presentation writer api, api to make presentation",
  },
  useCases: {
    title: "AI Presentation Use Cases",
    description: "Explore how professionals use AI to generate presentations, pitch decks, reports, and structured documents.",
    keywords: "ai presentations for business, pitch deck ai, ai pitch deck generator for sales, ai presentations for founders, ai presentations for education, ai pitch deck generator platform, AI Use Cases for Product, Engineering, and Docs, ai presentation for business, ai slides for Startups and Founders, ai presentation design",
  },
  auth: {
    title: "Login to Zynth AI | Access AI Creation Tools",
    description: "Log in to access AI-powered presentation and document creation tools designed for productivity.",
    keywords: "ai tools for document, ai tools for powerpoint presentation, ai tools for ppt, ai tool for powerpoint, ai tool for pdf, document ai tool, ai tool to make document, ppt ai generator, ppt ai maker, ai ppt presentation",
  },
  contactUs: {
    title: "Contact Zynth AI | Get Support or Partnerships",
    description: "Contact the Zynth AI team for support, partnerships, or questions related to AI presentations and documents.",
    keywords: "best ai app, the best ai app, best ai presentation, best ai presentation software, best ai for presentations, online ai tools, ai tools online, ai website for ppt, ai online platform, ai powered tools",
  },
  // Additional pages
  onboarding: {
    title: "Onboarding | Zynth AI",
    description: "Get started with Zynth AI and set up your account for creating AI-powered presentations and documents.",
  },
  organizationProfile: {
    title: "Organization Profile | Zynth AI",
    description: "Manage your organization profile and settings on Zynth AI.",
  },
  editOrganizationProfile: {
    title: "Edit Organization Profile | Zynth AI",
    description: "Update your organization profile and preferences on Zynth AI.",
  },
  presentationView: {
    title: "Presentation View | Zynth AI",
    description: "View and edit your presentations in Zynth AI.",
  },
  history: {
    title: "History | Zynth AI",
    description: "Access your presentation and document history on Zynth AI.",
  },
  refer: {
    title: "Refer a Friend | Zynth AI",
    description: "Refer your friends to Zynth AI and earn rewards.",
  },
  invoice: {
    title: "Invoice | Zynth AI",
    description: "View your invoices and billing information on Zynth AI.",
  },
  presentationShare: {
    title: "Presentation Share | Zynth AI",
    description: "Share your presentations created with Zynth AI.",
  },
  blog: {
    title: "Blog | Zynth AI",
    description: "Read articles about AI presentations, documents, and productivity tools.",
  },
  whiteLabelLanding: {
    title: "White Label Solutions | Zynth AI",
    description: "White label AI presentation tools for your business.",
  },
  useCasesSales: {
    title: "AI Presentations for Sales | Zynth AI",
    description: "Create compelling sales presentations using AI with Zynth.",
  },
  useCasesProduct: {
    title: "AI Presentations for Product Teams | Zynth AI",
    description: "Generate product presentations and documentation with AI.",
  },
  useCasesPitch: {
    title: "AI Pitch Deck Generator | Zynth AI",
    description: "Create investor-ready pitch decks using AI technology.",
  },
  useCasesMarketing: {
    title: "AI Marketing Presentations | Zynth AI",
    description: "Build marketing presentations and campaigns with AI.",
  },
  useCasesEmployee: {
    title: "AI Employee Communications | Zynth AI",
    description: "Create internal communications and training presentations with AI.",
  },
  useCasesProject: {
    title: "AI Project Presentations | Zynth AI",
    description: "Generate project reports and presentations using AI.",
  },
  useCasesBoard: {
    title: "AI Board Presentations | Zynth AI",
    description: "Create boardroom-ready presentations with AI.",
  },
  useCasesEducation: {
    title: "AI Presentations for Education | Zynth AI",
    description: "Build educational presentations and course materials with AI.",
  },
};

export const getPageSEO = (pageKey: string): SEOConfig => {
  return seoConfig[pageKey] || {
    title: "Zynth AI - AI Presentation & Document Builder",
    description: "Create AI-powered presentations and documents faster with Zynth AI.",
  };
};
